function safeText(value) {
   return String(value ?? "").trim();
}

function isValidHttpUrl(value) {
   const text = safeText(value);
   if (!text) return false;
   try {
      const url = new URL(text);
      return url.protocol === "http:" || url.protocol === "https:";
   } catch {
      return false;
   }
}

function isLikelyProfilePhotoUrl(value) {
   const text = safeText(value);
   if (!isValidHttpUrl(text)) return false;
   return /googleusercontent\.com|ggpht\.com|gravatar\.com|cloudinary\.com/i.test(
      text,
   );
}

const PHOTO_KEYS = [
   "picture",
   "photoURL",
   "photoUrl",
   "photo",
   "avatar",
   "avatarUrl",
   "avatarURL",
   "profilePicture",
   "profilePhoto",
   "profilePhotoUrl",
   "profileImage",
   "profileImageUrl",
   "googlePicture",
   "googleAvatar",
   "googleAvatarUrl",
   "oauthPicture",
   "image",
   "imageUrl",
   "pictureUrl",
];

const NESTED_PROFILE_KEYS = [
   "google",
   "oauth",
   "authProvider",
   "provider",
   "profile",
   "userProfile",
   "identity",
   "extras",
   "metadata",
   "meta",
];

const NESTED_PATHS = [
   ["profile", "picture"],
   ["profile", "photoURL"],
   ["profile", "photoUrl"],
   ["profile", "avatar"],
   ["userProfile", "picture"],
   ["userProfile", "photoURL"],
   ["extras", "picture"],
   ["extras", "photoURL"],
   ["extras", "avatar"],
   ["oauth", "picture"],
   ["google", "picture"],
   ["identity", "picture"],
];

function readPhotoFromObject(source) {
   if (!source || typeof source !== "object") return null;

   for (const key of PHOTO_KEYS) {
      const value = safeText(source[key]);
      if (isValidHttpUrl(value)) return value;
   }

   return null;
}

function readPhotoFromPaths(person) {
   for (const path of NESTED_PATHS) {
      let current = person;
      for (const key of path) {
         current = current?.[key];
      }
      const value = safeText(current);
      if (isValidHttpUrl(value)) return value;
   }
   return null;
}

function readPhotoFromIdentities(person) {
   const identities = person?.identities;
   if (!Array.isArray(identities)) return null;

   for (const identity of identities) {
      const direct = readPhotoFromObject(identity);
      if (direct) return direct;
   }

   return null;
}

function findPhotoUrlDeep(source, depth = 0, seen = new Set()) {
   if (!source || depth > 4) return null;
   if (typeof source === "string") {
      return isLikelyProfilePhotoUrl(source) ? safeText(source) : null;
   }
   if (typeof source !== "object") return null;
   if (seen.has(source)) return null;
   seen.add(source);

   for (const value of Object.values(source)) {
      if (typeof value === "string" && isLikelyProfilePhotoUrl(value)) {
         return safeText(value);
      }
      if (value && typeof value === "object") {
         const nested = findPhotoUrlDeep(value, depth + 1, seen);
         if (nested) return nested;
      }
   }

   return null;
}

/** Extrage URL-ul fotografiei Google/OAuth din obiectul utilizatorului. */
// Fotografia încărcată în platformă (personalul): serverul dă adresa relativă /api/avatars/<id>-<hex>.webp
// (avatarUrl) sau doar numele fișierului (avatarFile, în rândurile User). Adresa completă — pe serverul API.
const OWN_AVATAR_PATH_RE = /^\/api\/avatars\/\d+-[0-9a-f]{16}\.webp$/;
const OWN_AVATAR_FILE_RE = /^\d+-[0-9a-f]{16}\.webp$/;

function apiOrigin() {
   try {
      return new URL(process.env.REACT_APP_API_URL || window.location.origin).origin;
   } catch {
      return "";
   }
}

// Ultima schimbare de fotografie făcută în fila asta (id cont → adresă sau null = ștearsă): listele încărcate
// înainte (Personal, instructorii) nu știu de ea până la reîncărcare — profilul și cercurile o iau de aici.
// Fiecare schimbare crește versiunea și anunță abonații (AvatarUI), ca toate cercurile omului să se
// schimbe pe loc, nu după reîncărcare.
const changedInThisTab = new Map();
let avatarChangeVersion = 0;
const avatarChangeListeners = new Set();

export function rememberAvatarChange(userId, avatarPath) {
   const id = Number(userId);
   if (!Number.isInteger(id) || id <= 0) return;
   changedInThisTab.set(id, OWN_AVATAR_PATH_RE.test(safeText(avatarPath)) ? safeText(avatarPath) : null);
   avatarChangeVersion += 1;
   avatarChangeListeners.forEach((listener) => {
      try {
         listener();
      } catch {}
   });
}

/** Pentru useSyncExternalStore: abonare la schimbările de poză din fila asta. */
export function subscribeAvatarChanges(listener) {
   avatarChangeListeners.add(listener);
   return () => avatarChangeListeners.delete(listener);
}

export function getAvatarChangeVersion() {
   return avatarChangeVersion;
}

/** Contul cui e rândul: fișa instructorului are userId; rândul de User — id. */
function accountIdOf(person) {
   if (person.userId != null) return Number(person.userId);
   if (person.role != null || person.email != null) return Number(person.id);
   if (person.user?.id != null) return Number(person.user.id);
   return null;
}

// Pozele cunoscute din listele încărcate (GET /users: personal și elevi): id cont → fișier. Așa poza
// apare și unde omul vine fără câmpurile pozei (rezervări, lecții suplimentare, notificări…).
const knownAvatarFiles = new Map();

export function rememberKnownAvatars(list) {
   let changed = false;
   for (const row of Array.isArray(list) ? list : []) {
      const id = Number(row?.id);
      if (!Number.isInteger(id) || id <= 0) continue;
      const file = safeText(row?.avatarFile);
      const next = OWN_AVATAR_FILE_RE.test(file) ? file : null;
      if (knownAvatarFiles.get(id) !== next) {
         knownAvatarFiles.set(id, next);
         changed = true;
      }
   }
   if (changed) {
      avatarChangeVersion += 1;
      avatarChangeListeners.forEach((listener) => {
         try {
            listener();
         } catch {}
      });
   }
}

export function ownAvatarUrl(person) {
   if (!person || typeof person !== "object") return null;
   const accountId = accountIdOf(person);
   if (accountId && changedInThisTab.has(accountId)) {
      const changed = changedInThisTab.get(accountId);
      return changed ? `${apiOrigin()}${changed}` : null;
   }
   const path = safeText(person.avatarUrl);
   if (OWN_AVATAR_PATH_RE.test(path)) return `${apiOrigin()}${path}`;
   for (const file of [person.avatarFile, person.user?.avatarFile]) {
      const name = safeText(file);
      if (OWN_AVATAR_FILE_RE.test(name)) return `${apiOrigin()}/api/avatars/${name}`;
   }
   // omul vine fără câmpurile pozei: poza din listele încărcate
   const known = accountId ? knownAvatarFiles.get(accountId) : null;
   if (known) return `${apiOrigin()}/api/avatars/${known}`;
   return null;
}

export function resolveUserPhotoUrl(person) {
   if (!person || typeof person !== "object") return null;

   const own = ownAvatarUrl(person);
   if (own) return own;

   const direct = readPhotoFromObject(person);
   if (direct) return direct;

   const fromPaths = readPhotoFromPaths(person);
   if (fromPaths) return fromPaths;

   for (const key of NESTED_PROFILE_KEYS) {
      const nested = person?.[key];
      const nestedPhoto = readPhotoFromObject(nested);
      if (nestedPhoto) return nestedPhoto;
   }

   const fromIdentities = readPhotoFromIdentities(person);
   if (fromIdentities) return fromIdentities;

   return findPhotoUrlDeep(person);
}

// ── inițialele și culoarea avatarului (AvatarUI): aceleași peste tot ─────────
// Paleta pastelată de până acum (copiată în listele de elevi, grupe, personal):
// 9 nuanțe × 11 luminozități; culoarea se alege după nume, deci același om are
// aceeași culoare în orice listă.
const AVATAR_HUES = [
   { h: 70, s: 75 },
   { h: 0, s: 100 },
   { h: 30, s: 100 },
   { h: 54, s: 95 },
   { h: 130, s: 65 },
   { h: 210, s: 90 },
   { h: 255, s: 98 },
   { h: 285, s: 100 },
   { h: 330, s: 96 },
];
const AVATAR_LIGHTNESSES = [94, 92, 90, 88, 86, 84, 82, 80, 78, 76, 74];
export const AVATAR_COLORS = AVATAR_HUES.flatMap(({ h, s }) =>
   AVATAR_LIGHTNESSES.map((l) => `hsl(${h} ${s}% ${l}%)`),
);

function avatarName(source) {
   if (typeof source === "string") return safeText(source);
   const full = `${safeText(source?.firstName)} ${safeText(source?.lastName)}`.trim();
   return full || safeText(source?.name || source?.fullName || source?.email);
}

/** Inițialele: prima literă din prenume și din nume („Ana Popescu” → „AP”). */
export function avatarInitials(source) {
   if (source && typeof source === "object" && (source.firstName || source.lastName)) {
      const a = safeText(source.firstName).charAt(0);
      const b = safeText(source.lastName).charAt(0);
      if (a && b) return (a + b).toUpperCase();
      const one = safeText(source.firstName || source.lastName).slice(0, 2);
      return one ? one.toUpperCase() : "–";
   }
   const words = avatarName(source).split(/\s+/).filter(Boolean);
   if (!words.length) return "–";
   if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
   return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
}

/** Culoarea din paletă, după nume (fără nume: prima culoare). */
export function avatarColor(source) {
   const name = avatarName(source);
   if (!/\p{L}/u.test(name)) return AVATAR_COLORS[0];
   let h = 0;
   const text = name.normalize("NFKD");
   for (let i = 0; i < text.length; i += 1) h = (h * 31 + text.charCodeAt(i)) | 0;
   return AVATAR_COLORS[(h >>> 0) % AVATAR_COLORS.length];
}
